import type { FemaMeta } from 'types/fema.types';
import type { IpawsGreyableOption } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaEnvironment.utils';
import type { FemaEnvironmentChoice } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaEnvironment.config';
import { FEMA_ENVIRONMENT_OPTIONS } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaEnvironment.config';
import {
  greyOutIpawsOptions,
  isIpawsScopeAllowedForClass,
  isIpawsStatusAllowedForClass,
  resolveFemaEnvironmentClass,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaEnvironment.utils';

/**
 * RescAlert (Mike, 2026-09-26): TDL and DEMO offer every status and scope; PRODUCTION sends
 * Actual and Public only. The rule lives in femaEnvironment.utils and the Alert Creator
 * uses the same one; the helpers below apply it to the Global Action editor.
 */
export { isIpawsStatusAllowedForClass };

/** The finding for a status other than Actual in PRODUCTION. */
export const IPAWS_STATUS_CLASS_FINDING =
  'Only the Actual status is sent from this environment; choose Actual.';
/** The finding for a Restricted or Private scope in PRODUCTION. */
export const IPAWS_SCOPE_CLASS_FINDING =
  'Only the Public scope is sent from this environment; choose Public.';

/**
 * The FEMA class in play for the Global Action editor.
 * @returns The chosen class when the person chose one, else the class that the service serves,
 * else `null` while the meta is absent or names an unknown class.
 */
export const resolveIpawsFemaClass = (
  chosen: unknown,
  meta: Pick<FemaMeta, 'environment'> | null | undefined,
): FemaEnvironmentChoice | null => resolveFemaEnvironmentClass(chosen, meta);

/** Greys out every status but `Actual` unless the class is `tdl` or `demo`. */
export const getIpawsStatusOptionsForClass = <Option extends { value: string }>(
  options: readonly Option[],
  femaClass: FemaEnvironmentChoice | null,
): IpawsGreyableOption<Option>[] =>
  greyOutIpawsOptions(options, (value) => isIpawsStatusAllowedForClass(value, femaClass));

/** Greys out every scope but `Public` in the `production` class. */
export const getIpawsScopeOptionsForClass = <Option extends { value: string }>(
  options: readonly Option[],
  femaClass: FemaEnvironmentChoice | null,
): IpawsGreyableOption<Option>[] =>
  greyOutIpawsOptions(options, (value) => isIpawsScopeAllowedForClass(value, femaClass));

/**
 * The status finding for an action whose status is not Actual in PRODUCTION.
 * @returns The finding, or `null` when the status is allowed or the class is not known yet.
 */
export const getIpawsStatusClassFinding = (
  status: unknown,
  femaClass: FemaEnvironmentChoice | null,
): { fieldPath: 'status'; message: string } | null =>
  femaClass === null || isIpawsStatusAllowedForClass(status, femaClass)
    ? null
    : { fieldPath: 'status', message: IPAWS_STATUS_CLASS_FINDING };

/**
 * The scope finding for a Restricted or Private action in PRODUCTION.
 * @returns The finding, or `null` when the scope is allowed or the class is not known yet.
 */
export const getIpawsScopeClassFinding = (
  scope: unknown,
  femaClass: FemaEnvironmentChoice | null,
): { fieldPath: 'scope'; message: string } | null =>
  femaClass === null || isIpawsScopeAllowedForClass(scope, femaClass)
    ? null
    : { fieldPath: 'scope', message: IPAWS_SCOPE_CLASS_FINDING };

/** The class label when the class is not known. */
export const IPAWS_UNKNOWN_CLASS_LABEL = 'the FEMA environment that the service serves';

/**
 * The label of a FEMA class, for example `IPAWS LIVE`.
 * @returns The option label, else the served environment name, else a generic label.
 */
export const describeIpawsFemaClass = (
  femaClass: FemaEnvironmentChoice | null,
  meta: Pick<FemaMeta, 'environment'> | null | undefined,
): string =>
  FEMA_ENVIRONMENT_OPTIONS.find(({ value }) => value === femaClass)?.label ??
  (meta?.environment ? String(meta.environment) : IPAWS_UNKNOWN_CLASS_LABEL);

/** The builder row fields that the save confirmation reads. */
type IpawsSaveNoticeAction = {
  category?: string;
  formData?: { status?: unknown; femaEnvironment?: unknown } | null;
};

/**
 * The save confirmation sentence for an Actual IPAWS action (Mike, 2026-09-26).
 * Multi-COG contract §1: each action names its own class (`formData.femaEnvironment`). An action
 * with no class uses `classLabel`.
 * @returns The sentence when an IPAWS action has the status `Actual`, else `null`.
 */
export const getIpawsActualSaveNotice = (
  actions: readonly IpawsSaveNoticeAction[],
  classLabel: string,
): string | null => {
  const labels = [
    ...new Set(
      actions
        .filter(
          ({ category, formData }) =>
            category === 'Publish IPAWS Alert' && formData?.status === 'Actual',
        )
        .map(
          ({ formData }) =>
            FEMA_ENVIRONMENT_OPTIONS.find(({ value }) => value === formData?.femaEnvironment)
              ?.label ?? classLabel,
        ),
    ),
  ];
  return labels.length
    ? `This Global Action will send an ACTUAL IPAWS alert to ${labels.join(' and ')} when it triggers.`
    : null;
};
